<template>
  <div class="email-editor">
    <div id="email-editor"></div>
  </div>
</template>

<script>
import EditorJS from "@editorjs/editorjs";
import { Blocks } from "react-email-blocks";

import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import LinkTool from "@editorjs/link";
import SimpleImage from "@editorjs/simple-image";
import Table from "@editorjs/table";
import CodeTool from "@editorjs/code";
import List from "@editorjs/list";
import RawTool from "@editorjs/raw";
import Quote from "@editorjs/quote";
import Marker from "@editorjs/marker";
import Checklist from "@editorjs/checklist";
import InlineCode from "@editorjs/inline-code";
import Warning from "@editorjs/warning";
import Delimiter from "@editorjs/delimiter";
import ImageTool from "@editorjs/image";
import Embed from "@editorjs/embed";
// import SocialPost from "editorjs-social-post-plugin";

import createGenericInlineTool, {
  ItalicInlineTool,
  UnderlineInlineTool
} from "editorjs-inline-tool";

export default {
  data() {
    return {
      editor: null
    };
  },
  props: {
    onSave: {
      type: Function,
      required: true
    },
    data: {
      type: Object,
      required: false
    },
    editorType: {
      type: String,
      default: "page"
    }
  },
  created() {},
  mounted() {
    let tools = {};
    if (this.editorType === "page") {
      tools = {
        header: Header,
        paragraph: {
          class: Paragraph,
          inlineToolbar: true
        },
        bold: {
          class: createGenericInlineTool({
            sanitize: {
              strong: {}
            },
            shortcut: "CMD+B",
            tagName: "STRONG",
            toolboxIcon:
              '<svg class="icon icon--bold" width="12px" height="14px"><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#bold"></use></svg>'
          })
        },
        // or use a pre-defined tool instead
        italic: ItalicInlineTool,
        underline: UnderlineInlineTool,
        // underline: Underline,
        inlineCode: {
          class: InlineCode,
          shortcut: "CMD+SHIFT+M"
        },

        // socialPost: SocialPost,
        embed: {
          class: Embed,
          config: {
            services: {
              youtube: true,
              coub: true
            }
          }
        },
        warning: {
          class: Warning,
          inlineToolbar: true,
          shortcut: "CMD+SHIFT+W",
          config: {
            titlePlaceholder: "Title",
            messagePlaceholder: "Message"
          }
        },
        delimiter: Delimiter,

        checklist: {
          class: Checklist,
          inlineToolbar: true
        },
        imageURL: SimpleImage,
        // image: OddImage,
        image: {
          class: ImageTool,
          config: {
            endpoints: {
              byFile: "http://localhost:8008/uploadFile", // Your backend file uploader endpoint
              byUrl: "http://localhost:8008/fetchUrl" // Your endpoint that provides uploading by Url
            }
          }
        },
        code: CodeTool,
        raw: RawTool,
        list: {
          class: List,
          inlineToolbar: true
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          shortcut: "CMD+SHIFT+O",
          config: {
            quotePlaceholder: "Enter a quote",
            captionPlaceholder: "Quote's author"
          }
        },
        Marker: {
          class: Marker,
          shortcut: "CMD+SHIFT+M"
        },
        table: {
          class: Table,
          inlineToolbar: true,
          config: {
            rows: 2,
            cols: 3
          }
        },
        linkTool: {
          class: LinkTool,
          config: {
            endpoint: "https://meta-api.oddular.com/api/meta" // Your backend endpoint for url data fetching
          }
        }
      };
    } else if (this.editorType === "email") {
      tools = {
        Banner: Blocks.Banner.Editor,
        Button: Blocks.Button.Editor,
        disclaimer: Paragraph,
        FullImage: Blocks.FullImage.Editor,
        header: Header,
        ImageGrid: Blocks.ImageGrid.Editor,
        paragraph: Paragraph,
        Story: Blocks.Story.Editor
      };
    }

    this.editor = new EditorJS({
      /**
       * Id of Element that should contain the Editor
       */
      holder: "email-editor",
      logLevel: "ERROR",
      data: this.data,
      tools: tools,
      onChange: api => {
        this.editor.save().then(content => {
          !!this.onSave && this.onSave(content);
        });
      }
    });
  },
  methods: {},
  destroyed() {
    this.editor.destroy();
  }
};
</script>

<style lang="scss">
#email-editor {
  padding: 2.25rem 2rem;
  max-width: 800px;
  margin: 0 auto;

  .codex-editor {
    background: #fafafa;
    border-radius: 1rem;
    box-shadow: 0 0 5px rgba(25, 25, 25, 0.1);
  }

  .codex-editor--empty
    .ce-block:first-child
    .ce-paragraph[data-placeholder]:empty::before {
    display: inline-block;
    width: 100%;
    text-align: center;
  }

  .ce-block--focused {
    background: rgba(27, 135, 255, 0.1);
  }

  .codex-editor__redactor {
    padding-top: 15px;
    padding-bottom: 30px !important;
    border-radius: 0 0 1rem 1rem;

    &::after {
      width: 100%;
      background: #fafafa;
      color: rgba(0, 0, 0, 0.2);
      display: block;
      text-align: center;
      border-radius: 1rem;
      font-size: 1.25rem;
      content: "…";
      margin-bottom: -30px;
      height: 30px;
    }
  }

  .codex-editor--narrow .codex-editor__redactor {
    margin-right: 0 !important;
  }

  .ce-block__content,
  .ce-toolbar__content {
    max-width: 100%;
  }

  .ce-block__content {
    padding: 0 0.75rem;
  }

  .ce-header,
  .ce-paragraph {
    line-height: 1.6em;
    padding: 0.4rem 0;
    margin: 0;
  }

  .ce-header {
    font-weight: 700;
  }

  h1.ce-header {
    font-size: 1.55rem !important;
  }

  h2.ce-header {
    font-size: 1.45rem !important;
  }

  h3.ce-header {
    font-size: 1.35rem !important;
  }

  h4.ce-header {
    font-size: 1.25rem !important;
  }

  h5.ce-header {
    font-size: 1.15rem !important;
  }

  .ce-toolbar__actions {
    right: -22px;
    top: 10px;
  }

  .ce-toolbar {
    box-shadow: none;
  }

  .ce-toolbox {
    flex-wrap: wrap;
    background: none;
  }

  .codex-editor--narrow .ce-block--focused {
    padding-right: 0;
    margin-right: 0;
  }

  @media (max-width: 650px) {
    & {
      padding: 1rem 0.25rem;
      max-width: 800px;
      margin: 0 auto;
    }

    .codex-editor__redactor {
      padding-bottom: 45px !important;
    }

    .ce-toolbar {
      border: 0 solid transparent;
      background: transparent;
      padding: 0 10px;
    }

    .ce-toolbox {
      opacity: 1;
      visibility: visible;
      padding-right: 3rem;
    }

    .ce-block__content {
      max-width: 100%;
      width: 100%;
    }

    .ce-toolbar__content {
      background: white;
      border: 1px solid rgb(225, 225, 225);
      max-width: 100%;
      margin-top: 2px;
      width: 100%;
      border-radius: 0.75rem;
    }

    .ce-toolbar__actions {
      right: 10px;
      top: 16px;
    }

    .ce-toolbar__plus {
      display: none !important;
    }
  }

  .ce-toolbar__settings-btn {
    background: none;
    svg {
      height: 13px;
      width: 13px;
    }
  }

  @media (min-width: 651px) {
    .codex-editor--narrow .ce-toolbar__plus {
      left: -35px !important;
    }
    .codex-editor--narrow .ce-toolbar__actions {
      right: -22px !important;
      top: 12px;
    }
  }

  .ce-block {
    padding-top: 5px;
    padding-bottom: 7px;
    // border-bottom: 2px solid rgba(0, 0, 0, 0.075);
  }
  .ce-block:nth-child(odd) {
    background-color: rgba(0, 0, 0, 0.025);
  }
}
</style>
